import React, { ReactElement, useRef } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { COLORS, FONT } from '../shared';

export type ForYouTabKey = 'for-you' | 'events' | 'organizations';

interface TabDef {
  key: ForYouTabKey;
  label: string;
}

const TABS: TabDef[] = [
  { key: 'for-you', label: 'For You' },
  { key: 'events', label: 'Events' },
  { key: 'organizations', label: 'Organizations' },
];

export interface SubNavProps {
  active: ForYouTabKey;
  onChange: (tab: ForYouTabKey) => void;
  tabs?: ForYouTabKey[];
}

export const SubNav = ({
  active,
  onChange,
  tabs,
}: SubNavProps): ReactElement => {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const visibleTabs = tabs
    ? tabs
        .map(key => TABS.find(tab => tab.key === key))
        .filter((tab): tab is TabDef => Boolean(tab))
    : TABS;

  const focusTab = (index: number): void => {
    const clamped = (index + visibleTabs.length) % visibleTabs.length;
    tabRefs.current[clamped]?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        focusTab(index + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        focusTab(index - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusTab(0);
        break;
      case 'End':
        e.preventDefault();
        focusTab(visibleTabs.length - 1);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        onChange(visibleTabs[index].key);
        break;
      case 'Escape':
        e.preventDefault();
        tabRefs.current[index]?.blur();
        break;
      default:
        break;
    }
  };

  return (
    <Box
      role="tablist"
      aria-label="Home sections"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: { xs: 'space-between', md: 'flex-start' },
        gap: { xs: 0, md: '36px' },
        width: { xs: '300px', md: '100%' },
        maxWidth: '100%',
        mx: { xs: 'auto', md: 0 },
      }}
    >
      {visibleTabs.map((tab, index) => {
        const isActive = tab.key === active;
        return (
          <ButtonBase
            key={tab.key}
            ref={el => {
              tabRefs.current[index] = el;
            }}
            role="tab"
            id={`for-you-tab-${tab.key}`}
            aria-selected={isActive}
            aria-controls={`for-you-panel-${tab.key}`}
            tabIndex={isActive ? 0 : -1}
            disableRipple
            onClick={() => onChange(tab.key)}
            onKeyDown={e => handleKeyDown(e, index)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '4px',
              pb: 0,
              cursor: 'pointer',
              '&:focus-visible': {
                outline: `2px solid ${COLORS.bodyDark}`,
                outlineOffset: '2px',
              },
              '&:hover .SubNav-label': {
                color: isActive ? COLORS.bodyDark : COLORS.bodyDark,
              },
            }}
          >
            <Typography
              className="SubNav-label"
              sx={{
                fontFamily: FONT,
                fontWeight: isActive ? 600 : 500,
                fontSize: '16px',
                lineHeight: 'normal',
                color: isActive ? COLORS.bodyDark : COLORS.mutedAlt,
                whiteSpace: 'nowrap',
                transition: 'color 150ms ease',
              }}
            >
              {tab.label}
            </Typography>
            <Box
              sx={{
                height: '3px',
                width: '100%',
                backgroundColor: isActive ? COLORS.bodyDark : 'transparent',
                transform: isActive ? 'scaleX(1)' : 'scaleX(0)',
                transformOrigin: 'center',
                transition: 'transform 180ms ease',
              }}
            />
          </ButtonBase>
        );
      })}
    </Box>
  );
};
